import { useCallback } from "react";
import {
  ArrowLeft,
  Phone,
  MapPin,
  ShoppingBag,
  Star,
  MessageCircle,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "@cieda/utils";
import type { CustomerSummary } from "./types";
import {
  SEGMENT_STYLES,
  SEGMENT_LABELS,
  LOYALTY_STYLES,
  LOYALTY_LABELS,
} from "./constants";
import { TagsPanel } from "./TagsPanel";
import { NotesPanel } from "./NotesPanel";
import { BlockPanel } from "./BlockPanel";

// ---------------------------------------------------------------------------
// Customer Detail Panel
// ---------------------------------------------------------------------------

interface CustomerDetailProps {
  customer: CustomerSummary;
  onBack: () => void;
}

export function CustomerDetail({ customer, onBack }: CustomerDetailProps) {
  const handleWhatsApp = useCallback(() => {
    const phone = (customer.phone || "").replace(/\s/g, "").replace("+", "");
    window.open(`https://wa.me/${phone}`, "_blank");
    toast.success("Ouverture de WhatsApp...");
  }, [customer.phone]);

  const handleSMS = useCallback(() => {
    window.open(`sms:${(customer.phone || "").replace(/\s/g, "")}`, "_blank");
    toast.success("Ouverture SMS...");
  }, [customer.phone]);

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-stone-500 transition-colors hover:text-stone-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour à la liste
      </button>

      {/* Header card */}
      <div className="rounded-xl border border-stone-200 bg-white p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-100 font-display text-xl font-bold text-amber-800">
              {(customer.full_name || '?')[0]}
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-stone-900">
                {customer.full_name ?? 'Client sans nom'}
              </h2>
              <div className="mt-1 flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-stone-400" />
                <span className="text-sm text-stone-600">{customer.phone}</span>
              </div>
              {customer.email && (
                <p className="mt-0.5 text-xs text-stone-400">{customer.email}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${SEGMENT_STYLES[customer.segment]}`}>
              {SEGMENT_LABELS[customer.segment]}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full bg-stone-50 px-2.5 py-1 text-xs font-medium ${LOYALTY_STYLES[customer.loyalty_tier]}`}>
              <Star className="h-3 w-3" />
              {LOYALTY_LABELS[customer.loyalty_tier]}
            </span>
          </div>
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex flex-wrap gap-2 border-t border-stone-100 pt-4">
          <button
            onClick={handleWhatsApp}
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
          </button>
          <button
            onClick={handleSMS}
            className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
          >
            <Mail className="h-3.5 w-3.5" />
            SMS
          </button>
          <button
            className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-100"
          >
            <ShoppingBag className="h-3.5 w-3.5" />
            Voir commandes
          </button>
        </div>

        {/* Stats row */}
        <div className="mt-4 grid grid-cols-2 gap-4 border-t border-stone-100 pt-4 sm:grid-cols-5">
          <div>
            <p className="text-xs text-stone-400">Commandes</p>
            <p className="mt-0.5 text-lg font-semibold text-stone-900">{customer.total_orders}</p>
          </div>
          <div>
            <p className="text-xs text-stone-400">Total dépensé</p>
            <p className="mt-0.5 text-lg font-semibold text-stone-900">{formatFCFA(customer.total_spent_fcfa)}</p>
          </div>
          <div>
            <p className="text-xs text-stone-400">Dernière commande</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">{customer.last_order_date ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-stone-400">Région</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">{customer.region}</p>
          </div>
          <div>
            <p className="text-xs text-stone-400">Membre depuis</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">{customer.member_since}</p>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Contact & preferences */}
        <div className="space-y-3 rounded-xl border border-stone-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-stone-700">Détails</h3>
          <div className="space-y-2 text-sm">
            {customer.preferred_payment && (
              <div className="flex justify-between">
                <span className="text-stone-400">Paiement préféré</span>
                <span className="font-medium text-stone-800">{customer.preferred_payment}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-stone-400">Langue</span>
              <span className="font-medium text-stone-800">
                {customer.preferred_language === "fr" ? "Français" : customer.preferred_language === "wo" ? "Wolof" : "English"}
              </span>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="space-y-3 rounded-xl border border-stone-200 bg-white p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <MapPin className="h-4 w-4 text-stone-400" />
            Adresses
          </h3>
          {customer.addresses.length === 0 ? (
            <p className="text-xs italic text-stone-400">Aucune adresse enregistrée</p>
          ) : (
            <ul className="space-y-2">
              {customer.addresses.map((addr, i) => (
                <li key={i} className="text-sm">
                  <p className="font-medium text-stone-800">{addr.label}</p>
                  <p className="text-stone-500">{addr.full_address}, {addr.commune}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Tags — live from /api/admin/customers/:id/tags */}
        <TagsPanel customerId={customer.id} />

        {/* Notes — live from /api/admin/customers/:id/notes */}
        <NotesPanel customerId={customer.id} />
      </div>

      {/* Block / Unblock status card — fed by GET /api/admin/customers/:id */}
      <BlockPanel customerId={customer.id} />
    </div>
  );
}
